/* eslint-disable eslint-comments/no-restricted-disable */
import type { EntryPoint, EntryPointComponent } from 'react-relay';
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import { JSResourceForUserVisible } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import { JiraSiteAri } from '@atlassian/ari/jira/site';

import { fg } from '@atlassian/jira-feature-gating';
import type { JsmServicesEntrypointProps } from './src/JsmServices.entrypoint.types.tsx';

import jsmServicesDataQueryParameters from './src/__generated__/JsmServices_dataQuery$parameters.ts';
import type {
	JiraApplicationKey,
	JsmServices_dataQuery,
} from './src/__generated__/JsmServices_dataQuery.graphql.ts';

type Queries = { JsmServices_preloadedQuery: JsmServices_dataQuery };

const DEFAULT_APPLICATION_KEY: JiraApplicationKey = 'JIRA_SERVICE_DESK';

type JsmServicesEntryPointParams = {
	issueKey: string;
	cloudId: string;
	applicationKey?: JiraApplicationKey;
	issueViewAddAttachmentFlag?: boolean;
	tenantContext?: EntryPointRouteParams['tenantContext'];
};

// This function is a copy of getChildIssuesFieldIdsQueryVariables from '@jira/issue__main-issue-agg-query/src/getMainIssueViewAggQueryVariables.tsx'
const getChildIssuesFieldIdsQueryVariables = () => {
	const childIssuesFieldIds = ['issuetype'];

	if (!fg('relay-migration-edit-worklog') && !fg('jpx-93-unship-legacy-timetracking')) {
		childIssuesFieldIds.push('timetracking');
	}

	if (!fg('jira-issue-view-child-issues-panel-realtime-update')) {
		childIssuesFieldIds.push('status');
	}

	return childIssuesFieldIds;
};

// This function is adapted from getIssueSearchForChildIssueQueryVariables from '@jira/issue__main-issue-agg-query/src/getMainIssueViewAggQueryVariables.tsx'
const getIssueSearchForChildIssueQueryVariables = (issueKey: string | null | undefined) => ({
	issueSearchInput: fg('jira-issue-view-child-work-items-ssr-fg')
		? { jql: issueKey ? `parent = ${issueKey}` : '' }
		: { childIssuesInput: { parentIssueKey: issueKey || '' } },
	fieldSetsInput: {
		viewInput: { namespace: 'CHILD_ISSUE_PANEL', context: { issueContext: { issueKey } } },
	},
	childIssuesFieldIds: getChildIssuesFieldIdsQueryVariables(),
	childIssuesAmountOfColumns: 500,
	childIssuesFirst: null,
	childWorkItemsSSRUIEnabled: Boolean(fg('jira-issue-view-child-work-items-ssr-fg')),
	shouldUseNewChildWorkItemsDatafetcher: true,
	isInitialLoadQuery: true,
});

const getProjectKeyFromIssueKey = (issueKey: string | null | undefined): string =>
	issueKey ? issueKey.split('-')[0] : '';

type JsmServicesEntryPointComponent = EntryPointComponent<
	Queries,
	Record<string, never>,
	JsmServicesEntrypointProps,
	Record<string, never>
>;

export const jsmServicesEntryPoint: EntryPoint<
	JsmServicesEntryPointComponent,
	JsmServicesEntryPointParams
> = createEntryPoint({
	root: JSResourceForUserVisible<JsmServicesEntryPointComponent>(
		/* webpackChunkName: "async-jsm-services" */ './src/JsmServices.tsx',
		{
			entryFsSsrLiftImportToModule: true,
		},
	),
	getPreloadProps: ({
		issueKey,
		cloudId,
		applicationKey = DEFAULT_APPLICATION_KEY,
	}: JsmServicesEntryPointParams) => {
		return {
			queries: {
				JsmServices_preloadedQuery: {
					parameters: jsmServicesDataQueryParameters,
					variables: {
						cloudId: cloudId ?? '',
						issueKey: issueKey ?? '',
						projectKey: getProjectKeyFromIssueKey(issueKey),
						siteAri: cloudId ? JiraSiteAri.create({ siteId: cloudId }).toString() : '',
						forgeTypes: [],
						applicationKey,
						issueViewAddAttachmentFlag: false,
						...getIssueSearchForChildIssueQueryVariables(issueKey),
					},
				},
			},
		};
	},
});
